// components/JobDetails.js
import React, { useState } from "react";
import { ensureDateFormat } from "../utils/jobUtils";

const JobDetails = ({
  job,
  masterData,
  comparisonResults,
  onBack,
  onExport,
  hasPermission,
}) => {
  const [activeTab, setActiveTab] = useState("master");

  return (
    <div>
      {/* Job Header */}
      <JobHeader job={job} onBack={onBack} />

      {/* Tab Navigation */}
      <div className="job-tabs-container">
        <div className="job-tabs-header">
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              className={`job-tab ${activeTab === "master" ? "active" : ""}`}
              onClick={() => setActiveTab("master")}
            >
              📊 MASTER FILE ({masterData.length} รายการ)
            </button>
            <button
              className={`job-tab ${
                activeTab === "comparison" ? "active" : ""
              }`}
              onClick={() => setActiveTab("comparison")}
            >
              ⚖️ COMPARE FILE ({comparisonResults.length} รายการ)
            </button>
          </div>
          {hasPermission("canExportData") && (
            <ExportButtons job={job} onExport={onExport} />
          )}
        </div>

        <div className="job-table-wrapper">
          {activeTab === "master" ? (
            <MasterDataTab data={masterData} />
          ) : (
            <ComparisonTab data={comparisonResults} />
          )}
        </div>
      </div>
    </div>
  );
};

const JobHeader = ({ job, onBack }) => {
  return (
    <div className="card" style={{ marginBottom: "20px" }}>
      <div
        className="card-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h3 className="card-title">รายละเอียดงาน: {job.job_name}</h3>
          <p
            style={{
              margin: "8px 0 0 0",
              color: "#64748b",
              fontSize: "14px",
            }}
          >
            สร้างเมื่อ: {new Date(job.created_at).toLocaleDateString("th-TH")}
          </p>
        </div>
        <button className="btn btn-outline" onClick={onBack}>
          ← กลับไปรายการงาน
        </button>
      </div>
    </div>
  );
};

const ExportButtons = ({ job, onExport }) => {
  return (
    <div className="job-tab-export-buttons">
      <button
        className="job-tab-export-btn"
        onClick={() => onExport(job.id, "master")}
        title="ส่งออก MASTER FILE"
      >
        📥 ส่งออก MASTER
      </button>
      <button
        className="job-tab-export-btn"
        onClick={() => onExport(job.id, "comparison")}
        title="ส่งออก COMPARE FILE"
      >
        📥 ส่งออก COMPARE
      </button>
    </div>
  );
};

const MasterDataTab = ({ data }) => {
  if (data.length === 0) {
    return <EmptyState type="master" />;
  }

  return (
    <div>
      {/* Debug info - remove this in production */}
      <div className="job-debug-info">
        <strong>🔍 Debug Info:</strong>
        Master Data Length: {data.length} | Sample Keys:{" "}
        {data[0] ? Object.keys(data[0]).join(", ") : "No data"}
      </div>

      <div className="job-table-container">
        <table className="job-table">
          <thead>
            <tr>
              <th style={{ minWidth: "120px" }}>Outlet Code</th>
              <th style={{ minWidth: "200px" }}>Outlet Name</th>
              <th style={{ minWidth: "100px" }}>Site</th>
              <th style={{ minWidth: "120px" }}>Site Name</th>
              <th style={{ minWidth: "150px" }}>Type</th>
              <th style={{ minWidth: "120px" }}>SubType</th>
              <th style={{ minWidth: "100px" }}>Sales Route</th>
              <th style={{ minWidth: "100px" }}>Calendar Day</th>
              <th style={{ minWidth: "120px" }}>Invoice No</th>
              <th style={{ minWidth: "100px", textAlign: "right" }}>NIV</th>
              <th style={{ minWidth: "100px", textAlign: "right" }}>VAT</th>
              <th style={{ minWidth: "120px", textAlign: "right" }}>
                Total Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((record, index) => (
              <MasterDataRow key={index} record={record} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MasterDataRow = ({ record }) => {
  return (
    <tr>
      <td style={{ fontFamily: "monospace", fontWeight: "600" }}>
        {record["Outlet Code"] || record.outlet_code || "-"}
      </td>
      <td>{record["Outlet Name"] || record.outlet_name || "-"}</td>
      <td>{record.Site || record.site || "-"}</td>
      <td>{record["Site Name"] || record.site_name || "-"}</td>
      <td>
        {record["Outlet Type Description"] ||
          record.outlet_type_description ||
          "-"}
      </td>
      <td>
        {record["Outlet SubType Description"] ||
          record.outlet_subtype_description ||
          "-"}
      </td>
      <td style={{ fontFamily: "monospace" }}>
        {record["Sales Route"] || record.sales_route || "-"}
      </td>
      <td style={{ fontFamily: "monospace" }}>
        {ensureDateFormat(record["Calendar Day"] || record.calendar_day) || "-"}
      </td>
      <td style={{ fontFamily: "monospace", fontWeight: "600" }}>
        {record["Invoice No"] || record.invoice_no || "-"}
      </td>
      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
        {(parseFloat(record.NIV || record.niv) || 0).toLocaleString()}
      </td>
      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
        {(parseFloat(record.VAT || record.vat) || 0).toLocaleString()}
      </td>
      <td
        style={{
          textAlign: "right",
          fontFamily: "monospace",
          fontWeight: "600",
        }}
      >
        {(
          parseFloat(
            record["Total Sales Amt Inc. Tax"] || record.total_sales_amt_inc_tax
          ) || 0
        ).toLocaleString()}
      </td>
    </tr>
  );
};

const ComparisonTab = ({ data }) => {
  if (data.length === 0) {
    return <EmptyState type="comparison" />;
  }

  return (
    <div className="job-table-container">
      <table className="job-table">
        <thead>
          <tr>
            <th style={{ minWidth: "120px" }}>เลขที่ใบกำกับ (AR04)</th>
            <th style={{ minWidth: "120px" }}>Invoice No (MASTER)</th>
            <th style={{ minWidth: "100px" }}>วันที่ (AR04)</th>
            <th style={{ minWidth: "100px" }}>Calendar Day (MASTER)</th>
            <th style={{ minWidth: "100px" }}>รหัสลูกค้า</th>
            <th style={{ minWidth: "120px" }}>Outlet Code</th>
            <th style={{ minWidth: "150px" }}>Outlet Name</th>
            <th style={{ minWidth: "100px" }}>เลขที่บัญชี</th>
            <th style={{ minWidth: "100px" }}>Sales Route</th>
            <th style={{ minWidth: "120px", textAlign: "right" }}>
              ค่าสินค้า/บริการ
            </th>
            <th style={{ minWidth: "100px", textAlign: "right" }}>NIV</th>
            <th style={{ minWidth: "120px", textAlign: "right" }}>
              ภาษีมูลค่าเพิ่ม
            </th>
            <th style={{ minWidth: "100px", textAlign: "right" }}>VAT</th>
            <th style={{ minWidth: "200px" }}>ข้อผิดพลาด</th>
          </tr>
        </thead>
        <tbody>
          {data.map((record, index) => (
            <ComparisonRow key={index} record={record} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ComparisonRow = ({ record }) => {
  const hasError =
    record.Remark &&
    record.Remark !== "ตรงกันทุกประการ" &&
    record.Remark.trim() !== "";

  return (
    <tr
      style={{
        backgroundColor: hasError ? "#fef2f2" : "transparent",
      }}
    >
      <td style={{ fontFamily: "monospace", fontWeight: "600" }}>
        {record["เลขที่ใบกำกับ"] || "-"}
      </td>
      <td
        style={{
          fontFamily: "monospace",
          color: record["Invoice No"] ? "#059669" : "#dc2626",
        }}
      >
        {record["Invoice No"] || "-"}
      </td>
      <td style={{ fontFamily: "monospace" }}>{record["วันที่"] || "-"}</td>
      <td style={{ fontFamily: "monospace" }}>
        {record["Calendar Day"] || "-"}
      </td>
      <td style={{ fontFamily: "monospace" }}>{record["รหัสลูกค้า"] || "-"}</td>
      <td style={{ fontFamily: "monospace" }}>
        {record["Outlet Code"] || "-"}
      </td>
      <td>{record["Outlet Name"] || "-"}</td>
      <td style={{ fontFamily: "monospace" }}>
        {record["เลขที่บัญชี"] || "-"}
      </td>
      <td style={{ fontFamily: "monospace" }}>
        {record["Sales Route"] || "-"}
      </td>
      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
        {(parseFloat(record["ค่าสินค้า/บริการ"]) || 0).toLocaleString()}
      </td>
      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
        {(parseFloat(record["NIV"]) || 0).toLocaleString()}
      </td>
      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
        {(parseFloat(record["ภาษีมูลค่าเพิ่ม"]) || 0).toLocaleString()}
      </td>
      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
        {(parseFloat(record["VAT"]) || 0).toLocaleString()}
      </td>
      <td
        style={{
          fontSize: "12px",
          color: hasError ? "#dc2626" : "#059669",
          fontWeight: hasError ? "600" : "normal",
        }}
      >
        {record["Remark"] || "ตรงกันทุกประการ"}
      </td>
    </tr>
  );
};

const EmptyState = ({ type }) => {
  const config = {
    master: {
      icon: "📊",
      title: "ไม่มีข้อมูล MASTER FILE",
      description: "ยังไม่มีข้อมูล MASTER FILE สำหรับงานนี้",
    },
    comparison: {
      icon: "⚖️",
      title: "ไม่มีข้อมูลการเปรียบเทียบ",
      description: "ยังไม่มีผลการเปรียบเทียบสำหรับงานนี้",
    },
  };

  return (
    <div className="job-empty-state">
      <div style={{ fontSize: "48px", marginBottom: "16px" }}>
        {config[type].icon}
      </div>
      <h3>{config[type].title}</h3>
      <p>{config[type].description}</p>
    </div>
  );
};

export default JobDetails;
